import { mapAutocompleteAddress, resolvePostalCode } from './helpers';
import { tryCatch } from '@/utils/global';

/**
 * TEAM-15253: Google can resolve a picked suggestion such as "2520 El Camino Real, Carlsbad, CA" to
 * the road itself, so its place details carry no `street_number` (and often no ZIP) even though the
 * diner typed one. Geocoding the suggestion's own text returns the numbered address ("2520 S El
 * Camino Real, Carlsbad, CA 92008"). These helpers run that recovery and decide whether the geocoded
 * result is the same address the diner picked; anything that isn't is refused rather than guessed.
 *
 * TEAM-15164: the same geocode also recovers the ZIP for a numbered pick whose place details came
 * back without one ("424 Hahlo St, Houston, TX"), under the same matching rules.
 */

export type HouseNumberRecoveryFailure = 'no_result' | 'partial' | 'mismatch' | 'error';

export type HouseNumberRecovery =
  | { recovered: true; components: google.maps.GeocoderAddressComponent[] }
  | { recovered: false; outcome: HouseNumberRecoveryFailure };

/** `no_postal_code` — the suggestion geocoded to the same address, but with no valid ZIP. */
export type PostalCodeRecoveryFailure = HouseNumberRecoveryFailure | 'no_postal_code';

export type PostalCodeRecovery =
  | { recovered: true; postalCode: string }
  | { recovered: false; outcome: PostalCodeRecoveryFailure };

// "2520", "12B", "37-12" (Queens-style hyphenated numbers)
const HOUSE_NUMBER = /^\d+[a-z]?(?:-\d+[a-z]?)?$/i;

const DIRECTIONALS: Record<string, string> = {
  n: 'n',
  s: 's',
  e: 'e',
  w: 'w',
  ne: 'ne',
  nw: 'nw',
  se: 'se',
  sw: 'sw',
  north: 'n',
  south: 's',
  east: 'e',
  west: 'w',
  northeast: 'ne',
  northwest: 'nw',
  southeast: 'se',
  southwest: 'sw',
};

const STREET_TYPES: Record<string, string> = {
  street: 'st',
  avenue: 'ave',
  av: 'ave',
  boulevard: 'blvd',
  drive: 'dr',
  road: 'rd',
  lane: 'ln',
  court: 'ct',
  place: 'pl',
  parkway: 'pkwy',
  highway: 'hwy',
  terrace: 'ter',
  circle: 'cir',
  square: 'sq',
  trail: 'trl',
  expressway: 'expy',
  freeway: 'fwy',
};

const STREET_TYPE_ABBREVIATIONS = new Set(Object.values(STREET_TYPES));

// Own-property lookup so a token like "constructor" doesn't resolve to an Object.prototype member.
const lookup = (table: Record<string, string>, token: string) =>
  Object.prototype.hasOwnProperty.call(table, token) ? table[token] : undefined;

// Narrower than helpers.ts CITY_FALLBACK_TYPES: no county or colloquial area, so a county name
// can't pass as the city.
const CITY_TYPES = [
  'locality',
  'sublocality',
  'sublocality_level_1',
  'postal_town',
  'neighborhood',
  'administrative_area_level_3',
];

const tokenize = (value: string) =>
  value.toLowerCase().replace(/[.,#]/g, ' ').split(/\s+/).filter(Boolean);

/**
 * Splits a street name into its core ("el camino real") and any leading/trailing directional
 * ("s"), with street types abbreviated so "Market Street" and "Market St" compare equal. A
 * directional is only peeled off when a real name remains, so "North Ave" keeps "north".
 */
const parseStreet = (street: string) => {
  const tokens = tokenize(street).map((token) => lookup(STREET_TYPES, token) ?? token);
  const hasName = (rest: string[]) => rest.some((token) => !STREET_TYPE_ABBREVIATIONS.has(token));
  const directionals: string[] = [];

  const first = lookup(DIRECTIONALS, tokens[0]);
  if (tokens.length > 1 && first && hasName(tokens.slice(1))) {
    directionals.push(first);
    tokens.shift();
  }
  const last = lookup(DIRECTIONALS, tokens[tokens.length - 1]);
  if (tokens.length > 1 && last && hasName(tokens.slice(0, -1))) {
    directionals.push(last);
    tokens.pop();
  }

  return { core: tokens.join(' '), directionals };
};

/**
 * Google may add a directional the diner didn't type ("El Camino Real" → "S El Camino Real"), so a
 * one-sided directional is accepted; two different directionals (N vs S) are different streets.
 */
const streetsMatch = (typed: string, geocoded: string) => {
  const a = parseStreet(typed);
  const b = parseStreet(geocoded);
  if (!a.core || a.core !== b.core) return false;
  if (!a.directionals.length || !b.directionals.length) return true;
  return a.directionals.join(' ') === b.directionals.join(' ');
};

const componentNames = (components: google.maps.GeocoderAddressComponent[], types: string[]) =>
  components
    .filter((component) => component.types.some((type) => types.includes(type)))
    .flatMap((component) => [component.long_name, component.short_name])
    .map((name) => name.toLowerCase());

const splitMainText = (prediction: google.maps.places.AutocompletePrediction) => {
  const [first = '', ...rest] = (prediction.structured_formatting?.main_text ?? '')
    .trim()
    .split(/\s+/);
  return HOUSE_NUMBER.test(first) ? { houseNumber: first, street: rest.join(' ') } : undefined;
};

/** The house number the suggestion itself begins with ("2520 El Camino Real" → "2520"), if any. */
export const suggestionHouseNumber = (
  prediction: google.maps.places.AutocompletePrediction,
): string | undefined => splitMainText(prediction)?.houseNumber;

/**
 * True only when a geocoded result is the address the diner picked: not a partial match, the same
 * house number, the same street (see streetsMatch) and the same city as the suggestion.
 */
export const matchesSuggestion = (
  result: google.maps.GeocoderResult,
  prediction: google.maps.places.AutocompletePrediction,
): boolean => {
  const typed = splitMainText(prediction);
  const typedCity = (prediction.structured_formatting?.secondary_text ?? '').split(',')[0].trim();
  if (result.partial_match || !typed?.street || !typedCity) return false;

  const components = result.address_components ?? [];
  const numberMatches = componentNames(components, ['street_number']).includes(
    typed.houseNumber.toLowerCase(),
  );
  const streetMatches = componentNames(components, ['route']).some((name) =>
    streetsMatch(typed.street, name),
  );
  const cityMatches = componentNames(components, CITY_TYPES).includes(typedCity.toLowerCase());

  return numberMatches && streetMatches && cityMatches;
};

type SuggestionGeocode =
  | { matched: true; results: google.maps.GeocoderResult[] }
  | { matched: false; outcome: HouseNumberRecoveryFailure };

/**
 * Geocodes the suggestion's text and returns every result that matches it, in Google's order. Uses
 * the Geocoder directly (not use-places-autocomplete's getGeocode, which rejects with a bare status
 * string) and never throws — a ZERO_RESULTS rejection is reported as `no_result`.
 */
async function geocodeSuggestion(
  prediction: google.maps.places.AutocompletePrediction,
): Promise<SuggestionGeocode> {
  const [response, error] = await tryCatch(
    // Deferred so a synchronous throw (e.g. `google` undefined) becomes a rejection tryCatch catches.
    Promise.resolve().then(() =>
      new google.maps.Geocoder().geocode({ address: prediction.description }),
    ),
  );

  if (error) {
    // tryCatch wraps a bare-string rejection in an Error, so the status may be on `message`.
    const zeroResults =
      (error as { code?: string }).code === 'ZERO_RESULTS' || error.message === 'ZERO_RESULTS';
    return { matched: false, outcome: zeroResults ? 'no_result' : 'error' };
  }

  const results = response?.results ?? [];
  if (!results.length) {
    return { matched: false, outcome: 'no_result' };
  }

  const matches = results.filter((result) => matchesSuggestion(result, prediction));
  if (matches.length) {
    return { matched: true, results: matches };
  }

  // Only fuzzy guesses came back ('partial') vs. a real address that isn't the one picked
  // ('mismatch') — the diner sees the same refusal; the split is for telemetry.
  const onlyPartial = results.every((result) => result.partial_match);
  return { matched: false, outcome: onlyPartial ? 'partial' : 'mismatch' };
}

/** Geocodes the suggestion's text and returns the components of the first result that matches it. */
export async function recoverHouseNumber(
  prediction: google.maps.places.AutocompletePrediction,
): Promise<HouseNumberRecovery> {
  const geocoded = await geocodeSuggestion(prediction);
  if (!geocoded.matched) {
    return { recovered: false, outcome: geocoded.outcome };
  }

  return { recovered: true, components: geocoded.results[0].address_components };
}

/**
 * Geocodes the suggestion's text and returns the ZIP of the first matching result that has a valid
 * one (resolved exactly as a picked place's ZIP is). Only the ZIP is taken from the geocode; the
 * caller keeps the rest of the address the diner picked.
 */
export async function recoverPostalCode(
  prediction: google.maps.places.AutocompletePrediction,
): Promise<PostalCodeRecovery> {
  const geocoded = await geocodeSuggestion(prediction);
  if (!geocoded.matched) {
    return { recovered: false, outcome: geocoded.outcome };
  }

  for (const result of geocoded.results) {
    const postalCode = resolvePostalCode(mapAutocompleteAddress(result.address_components));
    if (postalCode) {
      return { recovered: true, postalCode };
    }
  }

  return { recovered: false, outcome: 'no_postal_code' };
}
